/**
 * Poprawki materiałów („Edytuj tekst” / „Edytuj układ”) zapisywane w bazie dla zmarłego.
 *
 * Klucz: formDataId (zmarły z historii formularzy) × rodzaj materiału × wzór (patrz patterns.js).
 * Poprawki są wspólne dla wszystkich kont zakładu. Zapis działa w tle, bez komunikatów dla użytkownika.
 * Serwer pilnuje numeru wersji - gdy w międzyczasie zapisał ktoś inny (409), ponawiamy zapis
 * na najnowszej wersji, więc wygrywa ostatnia zmiana.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import axios from '../../../axios';
import layouts from '../layouts/layouts';
import { fetchPattern, parseVariantKey } from './patterns';
import { rebaseTextOverrides } from './textMerge';

const SAVE_DEBOUNCE_MS = 800;
const MAX_CONFLICT_RETRIES = 3;

const customizationUrl = (formDataId, layoutType, variantKey) => (
    `/customizations/${formDataId}/${layoutType}/${encodeURIComponent(variantKey || 'builtin:0')}`
);

export const getCustomizationKey = (layoutType, variantKey) => `${layoutType}|${variantKey || 'builtin:0'}`;

/** Wszystkie poprawki zmarłego jako mapa { "typ|wzór": poprawka } */
export async function fetchCustomizations(formDataId) {
    if (!formDataId) return {};
    const response = await axios.get(`/customizations/${formDataId}`);
    return (response.data || []).reduce((map, item) => ({
        ...map,
        [getCustomizationKey(item.layoutType, item.variantKey)]: item,
    }), {});
}

/** Jednorazowy zapis poprawek innego wzoru (np. przeniesienie poprawek tekstu na nowo zapisany wzór) */
export function saveCustomizationOnce(formDataId, layoutType, variantKey, payload) {
    return axios.put(customizationUrl(formDataId, layoutType, variantKey), { ...payload, version: 0 });
}

/**
 * Poprawki tekstu nadpisane przez nowsze dane z formularza usuwamy z bazy na stałe (textMerge.js).
 * Wywoływane po wejściu do generowania - zwraca mapę poprawek po porządkach.
 * Gdy w międzyczasie ktoś inny zapisał poprawki (409) albo zapis się nie uda, zostaje stan z bazy
 * (podgląd i tak łączy teksty na bieżąco).
 */
export async function syncCustomizationsWithForm(formDataId, customizations, data) {
    if (!formDataId || !data) return customizations;
    const baseVariants = new Map();
    const getBaseVariant = async (variantKey) => {
        const parsed = parseVariantKey(variantKey);
        if (parsed?.kind !== 'pattern') return parsed?.id || 0;
        if (!baseVariants.has(parsed.id)) {
            baseVariants.set(parsed.id, fetchPattern(parsed.id).then(pattern => pattern?.baseVariant || 0));
        }
        return baseVariants.get(parsed.id);
    };

    const entries = await Promise.all(Object.entries(customizations).map(async ([key, item]) => {
        if (!item?.textOverrides || Object.keys(item.textOverrides).length === 0) return [key, item];
        try {
            const layout = layouts[item.layoutType]?.params?.[await getBaseVariant(item.variantKey)]?.layout;
            const { overrides, base } = rebaseTextOverrides({
                layout, data, textOverrides: item.textOverrides, textBase: item.textBase,
            });
            const unchanged = JSON.stringify(overrides) === JSON.stringify(item.textOverrides)
                && JSON.stringify(base) === JSON.stringify(item.textBase || {});
            if (unchanged) return [key, item];

            const response = await axios.put(customizationUrl(formDataId, item.layoutType, item.variantKey), {
                textOverrides: overrides,
                textBase: base,
                version: item.version || 0,
            });
            return [key, response.data];
        } catch (error) {
            console.error('Nie udało się uporządkować poprawek materiału:', error);
            return [key, item];
        }
    }));
    return Object.fromEntries(entries);
}

export function deleteCustomization(formDataId, layoutType, variantKey) {
    return axios.delete(customizationUrl(formDataId, layoutType, variantKey));
}

/** Wczytanie i autozapis poprawek jednego materiału */
export function useMaterialCustomization({ formDataId, layoutType, variantKey }) {
    const [customization, setCustomization] = useState(null);
    const [loaded, setLoaded] = useState(false);

    const versionRef = useRef(0);
    const pendingRef = useRef(null);
    const timerRef = useRef(null);
    const inFlightRef = useRef(null);
    const url = formDataId ? customizationUrl(formDataId, layoutType, variantKey) : null;

    useEffect(() => {
        let cancelled = false;
        if (!formDataId) {
            setLoaded(true);
            return undefined;
        }

        fetchCustomizations(formDataId)
            .then((map) => {
                if (cancelled) return;
                const current = map[getCustomizationKey(layoutType, variantKey)] || null;
                versionRef.current = current?.version || 0;
                setCustomization(current);
            })
            .catch((error) => {
                console.error('Nie udało się wczytać poprawek materiału:', error);
            })
            .finally(() => {
                if (!cancelled) setLoaded(true);
            });

        return () => { cancelled = true; };
    }, [formDataId, layoutType, variantKey]);

    const sendPending = useCallback(async () => {
        clearTimeout(timerRef.current);
        timerRef.current = null;

        // Jeden zapis naraz - kolejny wysyłamy z numerem wersji zwróconym przez poprzedni
        if (inFlightRef.current) {
            await inFlightRef.current;
        }
        const payload = pendingRef.current;
        if (!payload || !url) return;
        pendingRef.current = null;

        const request = (async () => {
            for (let attempt = 0; attempt <= MAX_CONFLICT_RETRIES; attempt += 1) {
                try {
                    const response = await axios.put(url, { ...payload, version: versionRef.current });
                    versionRef.current = response.data.version;
                    setCustomization(response.data);
                    return;
                } catch (error) {
                    const current = error?.response?.status === 409 ? error.response.data?.current : undefined;
                    if (current === undefined) {
                        // Błąd sieci/serwera - zmiany wracają do kolejki i pójdą z kolejnym zapisem
                        pendingRef.current = { ...payload, ...(pendingRef.current || {}) };
                        console.error('Nie udało się zapisać poprawek materiału:', error);
                        return;
                    }
                    // Ktoś inny zapisał w międzyczasie - ponawiamy na jego wersji
                    versionRef.current = current?.version || 0;
                }
            }
        })();

        inFlightRef.current = request.finally(() => {
            inFlightRef.current = null;
        });
        await inFlightRef.current;
    }, [url]);

    /** Dodaj zmiany do kolejki zapisu: { textOverrides, textBase } i/lub { layoutChanges } */
    const scheduleSave = useCallback((partial) => {
        if (!url) return;
        pendingRef.current = { ...(pendingRef.current || {}), ...partial };
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(sendPending, SAVE_DEBOUNCE_MS);
    }, [url, sendPending]);

    /** Wyślij zaległe zmiany od razu (np. przed wyjściem z edytora) */
    const flush = useCallback(() => sendPending(), [sendPending]);

    // Przy wyjściu z edytora wysyłamy zaległe zmiany (bez czekania na odpowiedź)
    useEffect(() => () => {
        if (pendingRef.current) {
            sendPending();
        }
    }, [sendPending]);

    return { customization, loaded, scheduleSave, flush };
}
